import { useStyleConfigV2 } from '@tectonic/elemason-components';
import { JSONSchemaForm } from '@tectonic/uikit';
import { useHydrated } from '@tectonic/utils';
import clsx from 'clsx';
import { has, isEmpty } from 'lodash-es';
import { forwardRef, useMemo } from 'react';
import { ElemasonFragmentProvider } from '../../contexts/ElemasonFragmentContext';
import { Cell } from '../../core/Cell';
import {
  useActionDispatch,
  usePageFragment,
  useSharedLocalState,
} from '../../hooks';
import { useFragmentValue } from '../../hooks/useFragmentValue';
import { useHaloScript } from '../../hooks/useHaloScript';

import type { ElemasonJSONSchemaFormWidget } from '@tectonic/types';
import type {
  JSONSchemaFormProps,
  RegistryWidgetsType,
  WidgetProps,
} from '@tectonic/uikit';
import type { FC, ForwardRefRenderFunction, PropsWithChildren } from 'react';

interface JSONSchemaFormWidgetProps {
  widget: ElemasonJSONSchemaFormWidget;
}

const ElemasonControl: FC<WidgetProps> = (props) => {
  const { id, label, name, onChange, options, uiSchema, schema, value } = props;
  const fragment = usePageFragment(uiSchema!.fragment);
  const fragmentValue = useFragmentValue(fragment);

  const data = {
    id,
    label,
    name,
    onChange,
    options,
    schema,
    value,
    uiSchema,
  };

  return (
    <ElemasonFragmentProvider value={fragmentValue(data)}>
      {fragment?.cells.map((cell, index) => (
        <Cell key={cell.id ?? index} cell={cell} />
      ))}
    </ElemasonFragmentProvider>
  );
};

const widgets: RegistryWidgetsType = {
  elemasonControl: ElemasonControl,
};

const ElemasonJsonSchemaForm: ForwardRefRenderFunction<
  any,
  PropsWithChildren<JSONSchemaFormProps>
> = ({ uiSchema, schema, ...props }, ref) => {
  const isHydrated = useHydrated();

  const layout = uiSchema?.['ui:layout'] ?? null;

  // fix layout
  const nSchema = useMemo(() => {
    if (!layout || isEmpty(layout)) {
      return schema;
    }

    const properties: any = {};
    const missingKeys = [];

    for (const key of layout) {
      if (has(schema, key)) {
        properties[key] = schema.properties?.[key];
      } else {
        missingKeys.push(key);
      }
    }

    // Add missing keys to rearranged object
    missingKeys.forEach((key) => {
      properties[key] = schema.properties?.[key];
    });
    return { ...schema, properties };
  }, [layout, schema]);

  return isHydrated ? (
    <JSONSchemaForm
      ref={ref}
      widgets={widgets}
      schema={nSchema}
      uiSchema={uiSchema}
      {...props}
    />
  ) : null;
};

const ExoticElemasonJsonSchemaForm = forwardRef(ElemasonJsonSchemaForm);

const JSONSchemaFormWidget: FC<JSONSchemaFormWidgetProps> = ({
  widget: { config, data, actions },
}) => {
  const dispatch = useActionDispatch();
  const schema = useHaloScript(data?.schema!);
  const uiSchema = useHaloScript(data?.uiSchema);
  const formData = useHaloScript(data?.formData!);
  const [style, className] = useStyleConfigV2(config?.container);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [fStyle, fClassName] = useStyleConfigV2(config?.form);

  const { sharedState, setSharedState } = useSharedLocalState(
    data?.stateKey!,
    formData
  );

  const onSubmit = (form: any) => {
    setSharedState(form.formData);

    actions?.onSubmit.forEach((action) => {
      dispatch(action);
    });
  };

  return (
    <div style={style} className={clsx('flex', className)}>
      <div style={fStyle} className={clsx('w-full', fClassName)}>
        <ExoticElemasonJsonSchemaForm
          schema={schema!}
          onSubmit={onSubmit}
          uiSchema={uiSchema}
          formData={formData ?? sharedState}
          className="rjsf w-full"
        />
      </div>
    </div>
  );
};

export {
  ExoticElemasonJsonSchemaForm as ElemasonJsonSchemaForm,
  JSONSchemaFormWidget,
};
